import { 
  MATERIAL_TYPES, 
  CONFIGURATION_WIDTH,
  checkMaterialAvailability 
} from '../firebase/inventoryService';

/**
 * Inventory Calculations Utility
 * Business logic for availability checking, material requirements, and suggestions
 */

// Material alternatives mapping for manual overrides
export const MATERIAL_ALTERNATIVES = {
  [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
  [MATERIAL_TYPES.OUTRIGGER_2M]: [MATERIAL_TYPES.OUTRIGGER_3M, MATERIAL_TYPES.OUTRIGGER_4M, MATERIAL_TYPES.OUTRIGGER_5M],
  [MATERIAL_TYPES.OUTRIGGER_3M]: [MATERIAL_TYPES.OUTRIGGER_2M, MATERIAL_TYPES.OUTRIGGER_4M, MATERIAL_TYPES.OUTRIGGER_5M],
  [MATERIAL_TYPES.OUTRIGGER_4M]: [MATERIAL_TYPES.OUTRIGGER_2M, MATERIAL_TYPES.OUTRIGGER_3M, MATERIAL_TYPES.OUTRIGGER_5M],
  [MATERIAL_TYPES.OUTRIGGER_5M]: [MATERIAL_TYPES.OUTRIGGER_2M, MATERIAL_TYPES.OUTRIGGER_3M, MATERIAL_TYPES.OUTRIGGER_4M]
};

// Configuration templates with default materials only (no priority system)
export const SCAFFOLD_CONFIGURATIONS = {
  // Single Width Platform Configurations
  'single-width-2m': {
    id: 'single-width-2m',
    name: 'Single Width 2m Platform',
    width: CONFIGURATION_WIDTH.SINGLE,
    platformHeightInMeter: '2m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.SW_FRAME, quantity: 2 },
      { materialType: MATERIAL_TYPES.SW_PLATFORM, quantity: 1 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 6 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 2 },
      { materialType: MATERIAL_TYPES.NARROW_BRACE, quantity: 1 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_5, quantity: 1 },
      { materialType: MATERIAL_TYPES.SW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.SW_GUARDFRAME, quantity: 2 },
      { materialType: MATERIAL_TYPES.BLUE_CAP, quantity: 4 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 8 }
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE] // User can manually change to jackbase
    }
  },
  'single-width-3m': {
    id: 'single-width-3m',
    name: 'Single Width 3m Platform',
    width: CONFIGURATION_WIDTH.SINGLE,
    platformHeightInMeter: '3m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.SW_FRAME, quantity: 4 },
      { materialType: MATERIAL_TYPES.SW_PLATFORM, quantity: 2 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 8 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 4 },
      { materialType: MATERIAL_TYPES.NARROW_BRACE, quantity: 2 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_5, quantity: 2 },
      { materialType: MATERIAL_TYPES.SW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 12 },
      { materialType: MATERIAL_TYPES.OUTRIGGER_2M, quantity: 4 } // Default outrigger
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
      [MATERIAL_TYPES.OUTRIGGER_2M]: [MATERIAL_TYPES.OUTRIGGER_3M, MATERIAL_TYPES.OUTRIGGER_4M, MATERIAL_TYPES.OUTRIGGER_5M]
    }
  },
  'single-width-4m': {
    id: 'single-width-4m',
    name: 'Single Width 4m Platform',
    width: CONFIGURATION_WIDTH.SINGLE,
    platformHeightInMeter: '4m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.SW_FRAME, quantity: 4 },
      { materialType: MATERIAL_TYPES.SW_PLATFORM, quantity: 2 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 8 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 4 },
      { materialType: MATERIAL_TYPES.NARROW_BRACE, quantity: 2 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_5, quantity: 2 },
      { materialType: MATERIAL_TYPES.SW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.SW_GUARDFRAME, quantity: 2 },
      { materialType: MATERIAL_TYPES.BLUE_CAP, quantity: 4 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 12 },
      { materialType: MATERIAL_TYPES.OUTRIGGER_2M, quantity: 4 } // Default outrigger
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
      [MATERIAL_TYPES.OUTRIGGER_2M]: [MATERIAL_TYPES.OUTRIGGER_3M, MATERIAL_TYPES.OUTRIGGER_4M, MATERIAL_TYPES.OUTRIGGER_5M]
    }
  },
  'single-width-5m': {
    id: 'single-width-5m',
    name: 'Single Width 5m Platform',
    width: CONFIGURATION_WIDTH.SINGLE,
    platformHeightInMeter: '5m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.SW_FRAME, quantity: 6 },
      { materialType: MATERIAL_TYPES.SW_PLATFORM, quantity: 3 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 10 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 6 },
      { materialType: MATERIAL_TYPES.NARROW_BRACE, quantity: 3 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_5, quantity: 3 },
      { materialType: MATERIAL_TYPES.SW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 16 },
      { materialType: MATERIAL_TYPES.OUTRIGGER_2M, quantity: 4 } // Default outrigger
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
      [MATERIAL_TYPES.OUTRIGGER_2M]: [MATERIAL_TYPES.OUTRIGGER_3M, MATERIAL_TYPES.OUTRIGGER_4M, MATERIAL_TYPES.OUTRIGGER_5M]
    }
  },
  'single-width-6m': {
    id: 'single-width-6m',
    name: 'Single Width 6m Platform',
    width: CONFIGURATION_WIDTH.SINGLE,
    platformHeightInMeter: '6m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.SW_FRAME, quantity: 6 },
      { materialType: MATERIAL_TYPES.SW_PLATFORM, quantity: 3 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 10 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 6 },
      { materialType: MATERIAL_TYPES.NARROW_BRACE, quantity: 3 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_5, quantity: 3 },
      { materialType: MATERIAL_TYPES.SW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.SW_GUARDFRAME, quantity: 2 },
      { materialType: MATERIAL_TYPES.BLUE_CAP, quantity: 4 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 16 },
      { materialType: MATERIAL_TYPES.OUTRIGGER_3M, quantity: 4 } // Default outrigger
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
      [MATERIAL_TYPES.OUTRIGGER_3M]: [MATERIAL_TYPES.OUTRIGGER_2M, MATERIAL_TYPES.OUTRIGGER_4M, MATERIAL_TYPES.OUTRIGGER_5M]
    }
  },
  'single-width-7m': {
    id: 'single-width-7m',
    name: 'Single Width 7m Platform',
    width: CONFIGURATION_WIDTH.SINGLE,
    platformHeightInMeter: '7m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.SW_FRAME, quantity: 8 },
      { materialType: MATERIAL_TYPES.SW_PLATFORM, quantity: 4 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 12 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 8 },
      { materialType: MATERIAL_TYPES.NARROW_BRACE, quantity: 4 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_5, quantity: 4 },
      { materialType: MATERIAL_TYPES.SW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 20 },
      { materialType: MATERIAL_TYPES.OUTRIGGER_3M, quantity: 4 } // Default outrigger
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
      [MATERIAL_TYPES.OUTRIGGER_3M]: [MATERIAL_TYPES.OUTRIGGER_2M, MATERIAL_TYPES.OUTRIGGER_4M, MATERIAL_TYPES.OUTRIGGER_5M]
    }
  },
  'single-width-8m': {
    id: 'single-width-8m',
    name: 'Single Width 8m Platform',
    width: CONFIGURATION_WIDTH.SINGLE,
    platformHeightInMeter: '8m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.SW_FRAME, quantity: 8 },
      { materialType: MATERIAL_TYPES.SW_PLATFORM, quantity: 4 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 12 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 8 },
      { materialType: MATERIAL_TYPES.NARROW_BRACE, quantity: 4 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_5, quantity: 4 },
      { materialType: MATERIAL_TYPES.SW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.SW_GUARDFRAME, quantity: 2 },
      { materialType: MATERIAL_TYPES.BLUE_CAP, quantity: 4 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 20 },
      { materialType: MATERIAL_TYPES.OUTRIGGER_4M, quantity: 4 } // Default outrigger
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
      [MATERIAL_TYPES.OUTRIGGER_4M]: [MATERIAL_TYPES.OUTRIGGER_2M, MATERIAL_TYPES.OUTRIGGER_3M, MATERIAL_TYPES.OUTRIGGER_5M]
    }
  },

  // Double Width Platform Configurations
  'double-width-2m': {
    id: 'double-width-2m',
    name: 'Double Width 2m Platform',
    width: CONFIGURATION_WIDTH.DOUBLE,
    platformHeightInMeter: '2m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.ENTRY_FRAME, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_FRAME, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_PLATFORM, quantity: 1 },
      { materialType: MATERIAL_TYPES.LANDING_PLATFORM, quantity: 1 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 6 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 2 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_8, quantity: 1 },
      { materialType: MATERIAL_TYPES.HANDRAIL, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_GUARDFRAME, quantity: 2 },
      { materialType: MATERIAL_TYPES.BLUE_CAP, quantity: 4 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 8 }
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE]
    }
  },
  'double-width-3m': {
    id: 'double-width-3m',
    name: 'Double Width 3m Platform',
    width: CONFIGURATION_WIDTH.DOUBLE,
    platformHeightInMeter: '3m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.ENTRY_FRAME, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_FRAME, quantity: 3 },
      { materialType: MATERIAL_TYPES.DW_PLATFORM, quantity: 2 },
      { materialType: MATERIAL_TYPES.LANDING_PLATFORM, quantity: 1 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 8 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 4 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_8, quantity: 1 },
      { materialType: MATERIAL_TYPES.HANDRAIL, quantity: 2 },
      { materialType: MATERIAL_TYPES.DW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 12 },
      { materialType: MATERIAL_TYPES.NARROW_BRACE, quantity: 1 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_5, quantity: 1 }
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE]
    }
  },
  'double-width-4m': {
    id: 'double-width-4m',
    name: 'Double Width 4m Platform',
    width: CONFIGURATION_WIDTH.DOUBLE,
    platformHeightInMeter: '4m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.ENTRY_FRAME, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_FRAME, quantity: 3 },
      { materialType: MATERIAL_TYPES.DW_PLATFORM, quantity: 2 },
      { materialType: MATERIAL_TYPES.LANDING_PLATFORM, quantity: 1 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 8 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 4 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_8, quantity: 2 },
      { materialType: MATERIAL_TYPES.HANDRAIL, quantity: 2 },
      { materialType: MATERIAL_TYPES.DW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_GUARDFRAME, quantity: 2 },
      { materialType: MATERIAL_TYPES.BLUE_CAP, quantity: 4 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 12 },
      { materialType: MATERIAL_TYPES.OUTRIGGER_2M, quantity: 4 } // Default outrigger
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
      [MATERIAL_TYPES.OUTRIGGER_2M]: [MATERIAL_TYPES.OUTRIGGER_3M, MATERIAL_TYPES.OUTRIGGER_4M, MATERIAL_TYPES.OUTRIGGER_5M]
    }
  },
  'double-width-5m': {
    id: 'double-width-5m',
    name: 'Double Width 5m Platform',
    width: CONFIGURATION_WIDTH.DOUBLE,
    platformHeightInMeter: '5m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.ENTRY_FRAME, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_FRAME, quantity: 5 },
      { materialType: MATERIAL_TYPES.DW_PLATFORM, quantity: 3 },
      { materialType: MATERIAL_TYPES.LANDING_PLATFORM, quantity: 1 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 10 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 6 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_8, quantity: 3 },
      { materialType: MATERIAL_TYPES.HANDRAIL, quantity: 3 },
      { materialType: MATERIAL_TYPES.DW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 16 },
      { materialType: MATERIAL_TYPES.OUTRIGGER_2M, quantity: 4 }, // Default outrigger
      { materialType: MATERIAL_TYPES.NARROW_BRACE, quantity: 1 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_5, quantity: 1 }
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
      [MATERIAL_TYPES.OUTRIGGER_2M]: [MATERIAL_TYPES.OUTRIGGER_3M, MATERIAL_TYPES.OUTRIGGER_4M, MATERIAL_TYPES.OUTRIGGER_5M]
    }
  },
  'double-width-6m': {
    id: 'double-width-6m',
    name: 'Double Width 6m Platform',
    width: CONFIGURATION_WIDTH.DOUBLE,
    platformHeightInMeter: '6m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.ENTRY_FRAME, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_FRAME, quantity: 5 },
      { materialType: MATERIAL_TYPES.DW_PLATFORM, quantity: 3 },
      { materialType: MATERIAL_TYPES.LANDING_PLATFORM, quantity: 1 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 10 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 6 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_8, quantity: 3 },
      { materialType: MATERIAL_TYPES.HANDRAIL, quantity: 3 },
      { materialType: MATERIAL_TYPES.DW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_GUARDFRAME, quantity: 2 },
      { materialType: MATERIAL_TYPES.BLUE_CAP, quantity: 4 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 16 },
      { materialType: MATERIAL_TYPES.OUTRIGGER_3M, quantity: 4 } // Default outrigger
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
      [MATERIAL_TYPES.OUTRIGGER_3M]: [MATERIAL_TYPES.OUTRIGGER_2M, MATERIAL_TYPES.OUTRIGGER_4M, MATERIAL_TYPES.OUTRIGGER_5M]
    }
  },
  'double-width-7m': {
    id: 'double-width-7m',
    name: 'Double Width 7m Platform',
    width: CONFIGURATION_WIDTH.DOUBLE,
    platformHeightInMeter: '7m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.ENTRY_FRAME, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_FRAME, quantity: 7 },
      { materialType: MATERIAL_TYPES.DW_PLATFORM, quantity: 4 },
      { materialType: MATERIAL_TYPES.LANDING_PLATFORM, quantity: 1 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 12 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 8 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_8, quantity: 4 },
      { materialType: MATERIAL_TYPES.HANDRAIL, quantity: 4 },
      { materialType: MATERIAL_TYPES.DW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 20 },
      { materialType: MATERIAL_TYPES.OUTRIGGER_3M, quantity: 4 }, // Default outrigger
      { materialType: MATERIAL_TYPES.NARROW_BRACE, quantity: 1 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_5, quantity: 1 }
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
      [MATERIAL_TYPES.OUTRIGGER_3M]: [MATERIAL_TYPES.OUTRIGGER_2M, MATERIAL_TYPES.OUTRIGGER_4M, MATERIAL_TYPES.OUTRIGGER_5M]
    }
  },
  'double-width-8m': {
    id: 'double-width-8m',
    name: 'Double Width 8m Platform',
    width: CONFIGURATION_WIDTH.DOUBLE,
    platformHeightInMeter: '8m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.ENTRY_FRAME, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_FRAME, quantity: 7 },
      { materialType: MATERIAL_TYPES.DW_PLATFORM, quantity: 4 },
      { materialType: MATERIAL_TYPES.LANDING_PLATFORM, quantity: 1 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 12 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 8 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_8, quantity: 4 },
      { materialType: MATERIAL_TYPES.HANDRAIL, quantity: 4 },
      { materialType: MATERIAL_TYPES.DW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_GUARDFRAME, quantity: 2 },
      { materialType: MATERIAL_TYPES.BLUE_CAP, quantity: 4 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 20 },
      { materialType: MATERIAL_TYPES.OUTRIGGER_4M, quantity: 4 } // Default outrigger
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
      [MATERIAL_TYPES.OUTRIGGER_4M]: [MATERIAL_TYPES.OUTRIGGER_2M, MATERIAL_TYPES.OUTRIGGER_3M, MATERIAL_TYPES.OUTRIGGER_5M]
    }
  },
  'double-width-9m': {
    id: 'double-width-9m',
    name: 'Double Width 9m Platform',
    width: CONFIGURATION_WIDTH.DOUBLE,
    platformHeightInMeter: '9m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.ENTRY_FRAME, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_FRAME, quantity: 9 },
      { materialType: MATERIAL_TYPES.DW_PLATFORM, quantity: 5 },
      { materialType: MATERIAL_TYPES.LANDING_PLATFORM, quantity: 1 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 14 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 10 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_8, quantity: 5 },
      { materialType: MATERIAL_TYPES.HANDRAIL, quantity: 5 },
      { materialType: MATERIAL_TYPES.DW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 24 },
      { materialType: MATERIAL_TYPES.OUTRIGGER_4M, quantity: 4 }, // Default outrigger
      { materialType: MATERIAL_TYPES.NARROW_BRACE, quantity: 1 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_5, quantity: 1 }
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
      [MATERIAL_TYPES.OUTRIGGER_4M]: [MATERIAL_TYPES.OUTRIGGER_2M, MATERIAL_TYPES.OUTRIGGER_3M, MATERIAL_TYPES.OUTRIGGER_5M]
    }
  },
  'double-width-10m': {
    id: 'double-width-10m',
    name: 'Double Width 10m Platform',
    width: CONFIGURATION_WIDTH.DOUBLE,
    platformHeightInMeter: '10m',
    requiredMaterials: [
      { materialType: MATERIAL_TYPES.ENTRY_FRAME, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_FRAME, quantity: 9 },
      { materialType: MATERIAL_TYPES.DW_PLATFORM, quantity: 5 },
      { materialType: MATERIAL_TYPES.LANDING_PLATFORM, quantity: 1 },
      { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 14 },
      { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: 10 },
      { materialType: MATERIAL_TYPES.STAIRS_LADDER_8, quantity: 5 },
      { materialType: MATERIAL_TYPES.HANDRAIL, quantity: 5 },
      { materialType: MATERIAL_TYPES.DW_TOEBOARD, quantity: 1 },
      { materialType: MATERIAL_TYPES.DW_GUARDFRAME, quantity: 2 },
      { materialType: MATERIAL_TYPES.BLUE_CAP, quantity: 4 },
      { materialType: MATERIAL_TYPES.WHEELS, quantity: 4 }, // Default base
      { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 24 },
      { materialType: MATERIAL_TYPES.OUTRIGGER_5M, quantity: 4 } // Default outrigger
    ],
    manualAlternatives: {
      [MATERIAL_TYPES.WHEELS]: [MATERIAL_TYPES.JACKBASE],
      [MATERIAL_TYPES.OUTRIGGER_5M]: [MATERIAL_TYPES.OUTRIGGER_2M, MATERIAL_TYPES.OUTRIGGER_3M, MATERIAL_TYPES.OUTRIGGER_4M]
    }
  }
};

/**
 * Calculate material requirements from invoice line items
 * @param {Array} lineItems - Array of line items from quotation/invoice
 * @returns {Object} Material requirements breakdown
 */
export const calculateMaterialRequirements = (lineItems) => {
  const requirements = {};
  const configurationBreakdown = [];
  
  if (!lineItems || !Array.isArray(lineItems)) {
    return { requirements: {}, configurationBreakdown: [], totalConfigurations: 0 };
  }
  
  lineItems.forEach((item, index) => {
    if (!item.workingHeight || !item.width) {
      console.warn(`Skipping line item ${index}: missing workingHeight or width`);
      return;
    }
    
    // Convert working height to platform height (subtract 2m)
    const workingHeight = parseFloat(item.workingHeight);
    if (isNaN(workingHeight) || workingHeight < 2) {
      console.warn(`Skipping line item ${index}: invalid working height ${item.workingHeight}`);
      return;
    }
    
    const platformHeight = workingHeight - 2;
    const width = item.width === 'double' ? CONFIGURATION_WIDTH.DOUBLE : CONFIGURATION_WIDTH.SINGLE;
    
    // Find matching configuration
    const configId = `${width}-width-${platformHeight}m`;
    const configuration = SCAFFOLD_CONFIGURATIONS[configId];
    
    if (!configuration) {
      console.warn(`No configuration found for ${configId}`);
      return;
    }
    
    // Add to breakdown
    configurationBreakdown.push({
      lineItemIndex: index,
      configurationId: configId,
      configurationName: configuration.name,
      workingHeight: workingHeight,
      platformHeight: platformHeight,
      width: width,
      platformHeightInMeter: configuration.platformHeightInMeter,
      length: 2,
      quantity: item.quantity || 1
    });
    
    // Calculate material requirements
    const quantity = item.quantity || 1;
    configuration.requiredMaterials.forEach(material => {
      if (material.quantity > 0) { // Only include materials with quantity > 0
        const materialType = material.materialType;
        const totalQuantity = material.quantity * quantity;
        
        if (!requirements[materialType]) {
          requirements[materialType] = {
            materialType,
            totalQuantity: 0,
            configurations: []
          };
        }
        
        requirements[materialType].totalQuantity += totalQuantity;
        requirements[materialType].configurations.push({
          configurationId: configId,
          configurationName: configuration.name,
          quantityPerConfig: material.quantity,
          configQuantity: quantity,
          totalQuantity: totalQuantity
        });
      }
    });
  });
  
  return {
    requirements,
    configurationBreakdown,
    totalConfigurations: configurationBreakdown.length
  };
};

/**
 * Check availability for all materials in a requirements object
 * @param {Object} requirements - Material requirements from calculateMaterialRequirements
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} Comprehensive availability check result
 */
export const checkRequirementsAvailability = async (requirements, startDate, endDate) => {
  try {
    const availabilityResults = {};
    const shortages = [];
    const suggestions = [];
    let overallAvailable = true;
    
    // Check availability for each material type
    for (const [materialType, requirement] of Object.entries(requirements)) {
      const availability = await checkMaterialAvailability(
        materialType,
        requirement.totalQuantity,
        startDate,
        endDate
      );
      
      availabilityResults[materialType] = availability;
      
      if (!availability.isAvailable) {
        overallAvailable = false;
        shortages.push({
          materialType,
          required: requirement.totalQuantity,
          available: availability.availableQuantity,
          shortage: availability.shortage
        });
        
        // Generate width alternatives for platforms
        const alternatives = await suggestWidthAlternatives(materialType, requirement.totalQuantity, startDate, endDate);
        if (alternatives.length > 0) {
          suggestions.push(...alternatives);
        }
      }
    }
    
    return {
      isAvailable: overallAvailable,
      availabilityResults,
      shortages,
      suggestions,
      checkedDateRange: { startDate, endDate },
      totalMaterialTypes: Object.keys(requirements).length
    };
  } catch (error) {
    console.error('Error checking requirements availability:', error);
    return {
      isAvailable: false,
      availabilityResults: {},
      shortages: [],
      suggestions: [],
      checkedDateRange: { startDate, endDate },
      totalMaterialTypes: 0,
      error: error.message
    };
  }
};

/**
 * Suggest width alternatives for scaffold materials
 * @param {string} materialType - Original material type
 * @param {number} quantity - Required quantity
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Array>} Array of alternative suggestions
 */
export const suggestWidthAlternatives = async (materialType, quantity, startDate, endDate) => {
  const suggestions = [];
  
  try {
    // Define width alternatives mapping
    const widthAlternatives = { 
      [MATERIAL_TYPES.DW_PLATFORM]: MATERIAL_TYPES.SW_PLATFORM,
    };
    
    const alternativeMaterial = widthAlternatives[materialType];
    if (alternativeMaterial) {
      const alternativeAvailability = await checkMaterialAvailability(
        alternativeMaterial,
        quantity,
        startDate,
        endDate
      );
      
      if (alternativeAvailability.isAvailable) {
        suggestions.push({
          originalMaterial: materialType,
          alternativeMaterial: alternativeMaterial,
          availableQuantity: alternativeAvailability.availableQuantity,
          requiredQuantity: quantity,
          suggestion: `Consider using ${alternativeMaterial} instead of ${materialType}`
        });
      }
    }
  } catch (error) {
    console.error('Error generating width alternatives:', error);
  }
  
  return suggestions;
};

/**
 * Calculate capacity utilization for a date range
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} Capacity utilization analysis
 */
export const calculateCapacityUtilization = async (startDate, endDate) => {
  try {
    const { getInventoryStats, getOverlappingReservations } = await import('../firebase/inventoryService');
    
    const inventoryStats = await getInventoryStats();
    const overlappingReservations = await getOverlappingReservations(startDate, endDate);
    
    const utilization = {};
    
    // Calculate utilization for each material type
    Object.entries(inventoryStats.byMaterialType).forEach(([materialType, stats]) => {
      const availableItems = stats.new + stats.used; // Exclude damaged items
      
      // Calculate reserved quantity for this material type
      let reservedQuantity = 0;
      overlappingReservations.forEach(reservation => {
        const materialReservation = reservation.reservedItems.find(
          item => item.materialType === materialType
        );
        if (materialReservation) {
          reservedQuantity += materialReservation.quantity;
        }
      });
      
      const utilizationPercentage = availableItems > 0 ? (reservedQuantity / availableItems) * 100 : 0;
      
      utilization[materialType] = {
        totalItems: stats.total,
        availableItems,
        reservedQuantity,
        utilizationPercentage: Math.round(utilizationPercentage * 100) / 100,
        status: utilizationPercentage > 80 ? 'high' : utilizationPercentage > 50 ? 'medium' : 'low'
      };
    });
    
    // Calculate overall utilization
    const totalAvailable = Object.values(utilization).reduce((sum, item) => sum + item.availableItems, 0);
    const totalReserved = Object.values(utilization).reduce((sum, item) => sum + item.reservedQuantity, 0);
    const overallUtilization = totalAvailable > 0 ? (totalReserved / totalAvailable) * 100 : 0;
    
    return {
      dateRange: { startDate, endDate },
      overallUtilization: Math.round(overallUtilization * 100) / 100,
      overallStatus: overallUtilization > 80 ? 'high' : overallUtilization > 50 ? 'medium' : 'low',
      byMaterialType: utilization,
      totalAvailableItems: totalAvailable,
      totalReservedItems: totalReserved,
      reservationCount: overlappingReservations.length
    };
  } catch (error) {
    console.error('Error calculating capacity utilization:', error);
    return {
      dateRange: { startDate, endDate },
      overallUtilization: 0,
      overallStatus: 'unknown',
      byMaterialType: {},
      totalAvailableItems: 0,
      totalReservedItems: 0,
      reservationCount: 0,
      error: error.message
    };
  }
};

/**
 * Validate if a reservation is feasible
 * @param {Object} reservationData - Reservation data to validate
 * @returns {Promise<Object>} Validation result
 */
export const validateReservationFeasibility = async (reservationData) => {
  try {
    const { startDate, endDate, reservedItems } = reservationData;
    
    if (!startDate || !endDate || !reservedItems || !Array.isArray(reservedItems)) {
      return {
        isValid: false,
        errors: ['Missing required reservation data'],
        warnings: []
      };
    }
    
    const errors = [];
    const warnings = [];
    const availabilityChecks = [];
    
    // Check availability for each reserved item
    for (const item of reservedItems) {
      const availability = await checkMaterialAvailability(
        item.materialType,
        item.quantity,
        startDate,
        endDate
      );
      
      availabilityChecks.push({
        materialType: item.materialType,
        requested: item.quantity,
        availability
      });
      
      if (!availability.isAvailable) {
        errors.push(`Insufficient ${item.materialType}: need ${item.quantity}, available ${availability.availableQuantity}`);
      }
      
      if (availability.availableQuantity < item.quantity * 1.1) { // Less than 10% buffer
        warnings.push(`Low availability for ${item.materialType}: only ${availability.availableQuantity} available`);
      }
    }
    
    // Validate date range
    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();
    
    if (start >= end) {
      errors.push('Start date must be before end date');
    }
    
    if (start < today) {
      warnings.push('Start date is in the past');
    }
    
    const daysDifference = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (daysDifference > 365) {
      warnings.push('Reservation period is longer than 1 year');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      availabilityChecks,
      reservationPeriodDays: daysDifference
    };
  } catch (error) {
    console.error('Error validating reservation feasibility:', error);
    return {
      isValid: false,
      errors: [`Validation error: ${error.message}`],
      warnings: [],
      availabilityChecks: [],
      reservationPeriodDays: 0
    };
  }
};

/**
 * Find available items for a specific material type and date range
 * @param {string} materialType - Material type to find
 * @param {number} quantity - Required quantity
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Array>} Array of available items
 */
export const findAvailableItems = async (materialType, quantity, startDate, endDate) => {
  try {
    const { getInventoryItems } = await import('../firebase/inventoryService');
    
    // Get all items of this material type that are not damaged
    const items = await getInventoryItems({
      materialType,
      status: ['new', 'used'] // Exclude damaged items
    });
    
    const availableItems = items.filter(item => 
      item.status === 'new' || item.status === 'used'
    );
    
    // Get overlapping reservations to exclude reserved items
    const { getOverlappingReservations } = await import('../firebase/inventoryService');
    const overlappingReservations = await getOverlappingReservations(startDate, endDate);
    
    // Get list of reserved item IDs for this material type
    const reservedItemIds = new Set();
    overlappingReservations.forEach(reservation => {
      const materialReservation = reservation.reservedItems.find(
        item => item.materialType === materialType
      );
      if (materialReservation && materialReservation.itemIds) {
        materialReservation.itemIds.forEach(itemId => reservedItemIds.add(itemId));
      }
    });
    
    // Filter out reserved items
    const trulyAvailableItems = availableItems.filter(item => 
      !reservedItemIds.has(item.id)
    );
    
    // Return up to the requested quantity
    return trulyAvailableItems.slice(0, quantity);
  } catch (error) {
    console.error('Error finding available items:', error);
    return [];
  }
};

/**
 * Get configuration by ID
 * @param {string} configurationId - Configuration ID
 * @returns {Object|null} Configuration object or null if not found
 */
export const getConfigurationById = (configurationId) => {
  return SCAFFOLD_CONFIGURATIONS[configurationId] || null;
};

/**
 * Get all available configurations
 * @returns {Array} Array of all configuration objects
 */
export const getAllConfigurations = () => {
  return Object.values(SCAFFOLD_CONFIGURATIONS);
};

/**
 * Get configurations by width
 * @param {string} width - 'single' or 'double'
 * @returns {Array} Array of configurations matching the width
 */
export const getConfigurationsByWidth = (width) => {
  return Object.values(SCAFFOLD_CONFIGURATIONS).filter(config => config.width === width);
};

/**
 * Get configurations by platformHeightInMeter
 * @param {string} platformHeightInMeter - platformHeightInMeter (e.g., '3m', '5m')
 * @returns {Array} Array of configurations matching the length
 */
export const getConfigurationsByPlatformHeightInMeter = (platformHeightInMeter) => {
  return Object.values(SCAFFOLD_CONFIGURATIONS).filter(config => config.platformHeightInMeter === platformHeightInMeter);
};

/**
 * Apply manual material overrides to requirements
 * @param {Object} requirements - Original material requirements
 * @param {Object} manualOverrides - Manual overrides object { originalMaterial: newMaterial }
 * @returns {Object} Updated requirements with overrides applied
 */
export const applyMaterialOverrides = (requirements, manualOverrides) => {
  if (!manualOverrides || Object.keys(manualOverrides).length === 0) {
    return requirements;
  }

  const updatedRequirements = { ...requirements };

  Object.entries(manualOverrides).forEach(([originalMaterial, newMaterial]) => {
    if (updatedRequirements[originalMaterial]) {
      const originalRequirement = updatedRequirements[originalMaterial];
      
      // Remove original material
      delete updatedRequirements[originalMaterial];
      
      // Add to new material (or create if doesn't exist)
      if (!updatedRequirements[newMaterial]) {
        updatedRequirements[newMaterial] = {
          materialType: newMaterial,
          totalQuantity: 0,
          configurations: []
        };
      }
      
      updatedRequirements[newMaterial].totalQuantity += originalRequirement.totalQuantity;
      updatedRequirements[newMaterial].configurations.push(...originalRequirement.configurations.map(config => ({
        ...config,
        materialType: newMaterial,
        isOverride: true,
        originalMaterial: originalMaterial
      })));
    }
  });

  return updatedRequirements;
};

/**
 * Get available alternatives for a material type
 * @param {string} materialType - Material type to get alternatives for
 * @returns {Array} Array of alternative material types
 */
export const getMaterialAlternatives = (materialType) => {
  return MATERIAL_ALTERNATIVES[materialType] || [];
};

/**
 * Check if a material can be manually overridden
 * @param {string} materialType - Material type to check
 * @returns {boolean} Whether the material can be overridden
 */
export const canOverrideMaterial = (materialType) => {
  return Object.keys(MATERIAL_ALTERNATIVES).includes(materialType);
};

/**
 * Get all materials that can be manually overridden
 * @returns {Array} Array of material types that support manual override
 */
export const getOverridableMaterials = () => {
  return Object.keys(MATERIAL_ALTERNATIVES);
};

/**
 * Validate manual overrides
 * @param {Object} manualOverrides - Manual overrides to validate
 * @returns {Object} Validation result with errors and warnings
 */
export const validateMaterialOverrides = (manualOverrides) => {
  const errors = [];
  const warnings = [];

  if (!manualOverrides || typeof manualOverrides !== 'object') {
    return { isValid: true, errors, warnings };
  }

  Object.entries(manualOverrides).forEach(([originalMaterial, newMaterial]) => {
    // Check if original material supports override
    if (!canOverrideMaterial(originalMaterial)) {
      errors.push(`Material ${originalMaterial} does not support manual override`);
      return;
    }

    // Check if new material is a valid alternative
    const alternatives = getMaterialAlternatives(originalMaterial);
    if (!alternatives.includes(newMaterial)) {
      errors.push(`${newMaterial} is not a valid alternative for ${originalMaterial}. Valid alternatives: ${alternatives.join(', ')}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Generate material requirements with manual override support
 * @param {Array} lineItems - Array of line items from quotation/invoice
 * @param {Object} manualOverrides - Optional manual overrides
 * @returns {Object} Material requirements with override support
 */
export const calculateMaterialRequirementsWithOverrides = (lineItems, manualOverrides = {}) => {
  // First calculate base requirements
  const baseResult = calculateMaterialRequirements(lineItems);
  
  // Validate overrides
  const validation = validateMaterialOverrides(manualOverrides);
  if (!validation.isValid) {
    console.warn('Invalid manual overrides:', validation.errors);
    return {
      ...baseResult,
      overrideErrors: validation.errors,
      overrideWarnings: validation.warnings
    };
  }

  // Apply overrides
  const updatedRequirements = applyMaterialOverrides(baseResult.requirements, manualOverrides);

  return {
    ...baseResult,
    requirements: updatedRequirements,
    appliedOverrides: manualOverrides,
    overrideErrors: validation.errors,
    overrideWarnings: validation.warnings
  };
};
